import FingerprintJS from '@fingerprintjs/fingerprintjs-pro'

/**
 * FingerprintService - Manages browser fingerprinting with caching
 *
 * Features:
 * - Caches visitor ID in localStorage to avoid repeated API calls
 * - Caches FingerprintJS agent instance in memory to avoid reloading scripts
 * - Handles edge cases: localStorage disabled, corrupted cache, network errors
 * - Automatic cache invalidation on errors
 */

// Constants
const STORAGE_KEY = 'visitor_id'
const CACHE_EXPIRY_KEY = 'visitor_id_expiry'
const CACHE_DURATION = 30 * 24 * 60 * 60 * 1000 // 30 days in milliseconds

// Module-level cache (persists across imports due to module singleton)
let fpAgent = null
let fpPromise = null

/**
 * Check if localStorage is available
 * @returns {boolean}
 */
function isLocalStorageAvailable() {
  try {
    const test = '__storage_test__'
    localStorage.setItem(test, test)
    localStorage.removeItem(test)
    return true
  } catch (e) {
    return false
  }
}

/**
 * Clear cached visitor ID
 */
function clearCache() {
  if (!isLocalStorageAvailable()) {
    return
  }

  try {
    localStorage.removeItem(STORAGE_KEY)
    localStorage.removeItem(CACHE_EXPIRY_KEY)
  } catch (error) {
    console.warn('Error clearing localStorage:', error)
  }
}

/**
 * Get cached visitor ID from localStorage
 * @returns {string|null}
 */
function getCachedVisitorId() {
  if (!isLocalStorageAvailable()) {
    return null
  }

  try {
    const visitorId = localStorage.getItem(STORAGE_KEY)
    const expiry = localStorage.getItem(CACHE_EXPIRY_KEY)

    // Check if cache exists and is not expired
    if (visitorId && expiry) {
      const expiryTime = parseInt(expiry, 10)
      const now = Date.now()

      if (now < expiryTime) {
        return visitorId
      }

      // Cache expired, clear it
      clearCache()
    }

    return null
  } catch (error) {
    console.warn('Error reading from localStorage:', error)
    return null
  }
}

/**
 * Cache visitor ID in localStorage
 * @param {string} visitorId
 */
function cacheVisitorId(visitorId) {
  if (!isLocalStorageAvailable() || !visitorId) {
    return
  }

  try {
    const expiry = Date.now() + CACHE_DURATION
    localStorage.setItem(STORAGE_KEY, visitorId)
    localStorage.setItem(CACHE_EXPIRY_KEY, expiry.toString())
  } catch (error) {
    console.warn('Error writing to localStorage:', error)
    // If quota exceeded, try to clear old data
    if (error.name === 'QuotaExceededError') {
      clearCache()
    }
  }
}

/**
 * Load FingerprintJS agent (cached in memory)
 * @returns {Promise<Object>}
 */
async function loadFingerprintAgent() {
  // Return cached agent if available
  if (fpAgent) {
    return fpAgent
  }

  // Return existing promise if load is already in progress
  if (fpPromise) {
    return fpPromise
  }

  // Load new agent
  fpPromise = FingerprintJS.load({
    apiKey: 'FshXOiyrz3LRwMoLnXsG',
    endpoint: [
      'https://fingerprint.udara360.io',
      FingerprintJS.defaultEndpoint,
    ],
    scriptUrlPattern:
      'https://fingerprint.udara360.io/web/v<version>/<apiKey>/loader_v<loaderVersion>.js',
  })

  try {
    fpAgent = await fpPromise
    return fpAgent
  } catch (error) {
    // Clear promise cache on error to allow retry
    fpPromise = null
    throw error
  }
}

/**
 * Create a promise that rejects after a timeout
 * @param {number} ms
 * @returns {Promise}
 */
function withTimeout(promise, ms) {
  let timer
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error('Fingerprint request timed out')), ms)
  })
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer))
}

/**
 * Get visitor ID with caching strategy:
 * 1. Check localStorage cache first
 * 2. If not found or expired, fetch from FingerprintJS
 * 3. Cache the result
 *
 * @returns {Promise<string>}
 */
export async function getVisitorId() {
  // Try to get from cache first
  const cachedId = getCachedVisitorId()
  if (cachedId) {
    return cachedId
  }

  // If not in cache, fetch from FingerprintJS with a 10s timeout
  try {
    const agent = await withTimeout(loadFingerprintAgent(), 10000)
    const { visitorId } = await withTimeout(agent.get(), 10000)

    // Cache the result
    cacheVisitorId(visitorId)

    return visitorId
  } catch (error) {
    console.error('Error getting visitor ID:', error)

    // On error, clear any corrupted cache
    clearCache()

    // Re-throw to let caller handle
    throw new Error('Failed to get device fingerprint. Please try again.')
  }
}

/**
 * Reset the service (useful for testing or when user logs out)
 */
export function reset() {
  clearCache()
  fpAgent = null
  fpPromise = null
}

// Default export for convenience
export default {
  getVisitorId,
  reset,
}
